import React from 'react';
import { Typography as AntType } from 'antd';
import classNames from 'classnames';
/**
 * @module Typography
 * @description A custom wrapper around Ant Design's Typography component with additional styling options
 */

/**
 * @typedef {Object} TitleProps
 * @property {string} [weight] - Font weight class to apply
 * @property {number} [visualLevel] - Visual heading level (1-6)
 * @property {number} [level] - Semantic heading level (1-6)
 * @property {React.ReactNode} children - Child elements
 */

/**
 * @typedef {Object} TextProps
 * @property {string} [size] - Text size class to apply
 * @property {string} [weight] - Font weight class to apply
 * @param {string} [align] - text alignment - also changes display to block ('left' | 'center' | 'right').
 * @property {React.ReactNode} children - Child elements
 */

/**
 * @typedef {Object} ParagraphProps
 * @property {string} [weight] - Font weight class to apply
 * @property {React.ReactNode} children - Child elements
 */

/**
 * @typedef {Object} LinkProps
 * @property {string} [weight] - Font weight class to apply
 * @property {React.ReactNode} children - Child elements
 */

/**
 * @component
 * @param {Object} props
 * @param {React.ReactNode} props.children - Child elements
 * @returns {JSX.Element} Typography component
 */

/**
 * @component
 * @name Typography.Title
 * @param {TitleProps} props
 * @returns {JSX.Element} Title component with configurable weight, level and italic styling
 */

/**
 * @component
 * @name Typography.Text
 * @param {TextProps} props
 * @returns {JSX.Element} Text component with configurable size and weight
 */

/**
 * @component
 * @name Typography.Paragraph
 * @param {ParagraphProps} props
 * @returns {JSX.Element} Paragraph component with configurable weight
 */

/**
 * @component
 * @name Typography.Link
 * @param {LinkProps} props
 * @returns {JSX.Element} Link component with configurable weight
 */
const subcomponents = {
    Title: ( { weight, children, italic, visualLevel, ...restProps } ) => {
        if ( !visualLevel ) {
            visualLevel = restProps.level ? restProps.level : 1;
        }
        if ( restProps.level === 6 && italic ) {
            return (
                <h6 
                { ...restProps }
                className={ classNames(
                    'ant-typography',
                    `ant-typography-heading-${ visualLevel }`,
                    weight ? `ant-typography-${ weight }` : '',
                    restProps.className
                ) }
                >
                    <i>{ children }</i>
                </h6>
            );
        } else if ( restProps.level === 6 && !italic ) {
            return (
                <h6 
                { ...restProps }
                className={ classNames(
                    'ant-typography',
                    `ant-typography-heading-${ visualLevel }`,
                    weight ? `ant-typography-${ weight }` : '',
                    restProps.className
                ) }
                >
                    { children }
                </h6>
            );
        } else {
            return (

                <AntType.Title
                    { ...restProps } 
                    className={ classNames(
                        weight ? `ant-typography-${ weight }` : '',
                        visualLevel ? `ant-typography-heading-${ visualLevel }` : '',
                        restProps.className
                    ) } 
                    >
                    { children }
                </AntType.Title>

            );
        }
    },
    Text: ( { size, weight, children, align, ...restProps } ) => (
        <AntType.Text
            { ...restProps }
            className={ classNames(
                weight ? `ant-typography-${ weight }` : '',
                size ? `ant-typography-size-${ size }` : '',
                restProps.className,
                align
            ) }
        >
            { children }
        </AntType.Text>
    ),
    Paragraph: ( { weight, children, ...restProps } ) => (
        <AntType.Paragraph
            { ...restProps }
            className={ classNames(
                weight ? `ant-typography-${ weight }` : '',
                { [ restProps.className ]: restProps.className ? true : false }
            ) }
            >
            { children }
        </AntType.Paragraph>
    ),
    Link: ( { size, weight, children, ...restProps } ) => (
        <AntType.Link
            { ...restProps }
            className={ classNames(
                weight ? `ant-typography-${ weight }` : '',
                size ? `ant-typography-size-${ size }` : '',
                { [ restProps.className ]: restProps.className ? true : false }
            ) }
            >
            { children }
        </AntType.Link>
    )
};

const Typography = ( { children, ...restProps } ) => {
    return <AntType { ...restProps }>{ children }</AntType>;
};

Object.keys( subcomponents ).forEach( component => {
    Typography[ component ] = subcomponents[ component ];
} );

export default Typography;
